import { TimeBucket, TimeBucket_TimeSeries } from "../../../../proto/generated/common";
import { RowLayout } from "./fixedRangeVolumeProfile";


export function getVPFRTimeframeFromBucket(
    chartBucket: TimeBucket,
    periodMinutes: number,
    useMarketHours: boolean = true,
    /** Regular-session length in minutes from trading calendar; used when useMarketHours is true. */
    regularSessionMinutes?: number
): TimeBucket {
    const oneDayMinutes = useMarketHours ? (regularSessionMinutes && regularSessionMinutes > 0 ? regularSessionMinutes : 1440) : 1440;

    const sequence = [
        {
            label: '1',
            minutes: 1,
            timeBucket: {
                timeSeries: TimeBucket_TimeSeries.MINUTE,
                value: 1,
            }
        },
        {
            label: '5',
            minutes: 5,
            timeBucket: {
                timeSeries: TimeBucket_TimeSeries.MINUTE,
                value: 5,
            }
        },
        {
            label: '15',
            minutes: 15,
            timeBucket: {
                timeSeries: TimeBucket_TimeSeries.MINUTE,
                value: 15,
            }
        },
        {
            label: '30',
            minutes: 30,
            timeBucket: {
                timeSeries: TimeBucket_TimeSeries.MINUTE,
                value: 30,
            }
        },
        {
            label: '60',
            minutes: 60,
            timeBucket: {
                timeSeries: TimeBucket_TimeSeries.HOUR,
                value: 1,
            }
        },
        {
            label: '240',
            minutes: 240,
            timeBucket: {
                timeSeries: TimeBucket_TimeSeries.HOUR,
                value: 4,
            }
        },
        {
            label: '1D',
            minutes: oneDayMinutes,
            timeBucket: {
                timeSeries: TimeBucket_TimeSeries.DAY,
                value: 1,
            }
        },
    ];

    const isSecondBased = chartBucket.timeSeries === TimeBucket_TimeSeries.SECOND;

    // Rule 1: second-based or <= 5 minutes total range → use 1S
    if (isSecondBased || periodMinutes <= 5) return {
        timeSeries: TimeBucket_TimeSeries.SECOND,
        value: 1,
    };

    // Rule 2: find first timeframe in sequence with <5000 bars
    for (const tf of sequence) {
        const barsCount = periodMinutes / tf.minutes;
        if (barsCount < 5000) return tf.timeBucket;
    }

    // Rule 3: fallback to chart timeframe
    return chartBucket
}


/**
 * Elapsed minutes between start/end that fall inside the regular session window.
 * Pass session open/close as seconds-of-day from the trading calendar (no hardcoded hours).
 */
export function getMarketMinutesBetween(
    start: Date,
    end: Date,
    sessionStartSecondsOfDay = 0,
    sessionEndSecondsOfDay = 24 * 60 * 60
): number {
    const MINUTES_PER_DAY = Math.max(0, (sessionEndSecondsOfDay - sessionStartSecondsOfDay) / 60);

    if (end <= start) return 0;

    let totalMinutes = 0;
    let current = new Date(start);

    while (current < end) {
        const dayOpen = new Date(current);
        dayOpen.setHours(0, 0, 0, 0);
        dayOpen.setTime(dayOpen.getTime() + sessionStartSecondsOfDay * 1000);
        const dayClose = new Date(current);
        dayClose.setHours(0, 0, 0, 0);
        dayClose.setTime(dayClose.getTime() + sessionEndSecondsOfDay * 1000);

        // If end is before market open, skip day
        if (end <= dayOpen) break;

        // Compute overlap for this day
        const effectiveStart = current < dayOpen ? dayOpen : current;
        const effectiveEnd = end < dayClose ? end : dayClose;

        if (effectiveEnd > effectiveStart) {
            totalMinutes += (effectiveEnd.getTime() - effectiveStart.getTime()) / (1000 * 60);
        }

        // Move to next calendar day 00:00
        current = new Date(dayOpen);
        current.setDate(current.getDate() + 1);
        current.setHours(0, 0, 0, 0);
    }

    // Clamp to market day maximum
    if (totalMinutes > MINUTES_PER_DAY * 3650) {
        // sanity cap in case of weird inputs
        totalMinutes = Math.round(totalMinutes);
    }

    return totalMinutes;
}

export function getHistogramRowCount(
    histogramTop: number,
    histogramBottom: number,
    tickSize: number,
    rowLayout: RowLayout,
    rowSize: number
): number {
    const totalTicks = Math.round((histogramTop - histogramBottom) / tickSize);
    if (totalTicks <= 0) return 0;

    if (rowLayout === 'TicksPerRow') {
        // Case 1: Row size = ticks per row
        return Math.ceil(totalTicks / rowSize);
    }

    // Case 2: Row size = number of rows
    // Compute ideal ticks per row (can be fractional)
    const ticksPerRowFloat = totalTicks / rowSize;

    const floorTicks = Math.floor(ticksPerRowFloat);
    const ceilTicks = Math.ceil(ticksPerRowFloat);

    // Calculate total rows for both rounding options
    const rowsIfFloor = Math.ceil(totalTicks / floorTicks);
    const rowsIfCeil = Math.ceil(totalTicks / ceilTicks);

    // Pick whichever rounding gives closer count to requested rowSize
    const diffFloor = Math.abs(rowsIfFloor - rowSize);
    const diffCeil = Math.abs(rowsIfCeil - rowSize);

    let chosenTicksPerRow, actualRows;

    if (diffFloor < diffCeil) {
        chosenTicksPerRow = floorTicks;
        actualRows = rowsIfFloor;
    } else {
        chosenTicksPerRow = ceilTicks;
        actualRows = rowsIfCeil;
    }

    return actualRows;
}